import { AutocompleteInputChangeReason, AutocompleteProps } from '@mui/material';
import { useLocalStorage } from '@rehooks/local-storage';
import { isEmpty, isNil } from 'ramda';
import { useEffect, useMemo, useState } from 'react';
import { useSearchBox } from 'react-instantsearch';
import { useNavigate } from 'react-router-dom';

import { SearchDomain, searchDomains } from '../../types/SearchDomain.ts';
import { LOCAL_STORAGE_PREFIX, LocalStorageKeys } from '~/common/constants/localStorage.ts';
import { RouteId } from '~/routes/RouteId.ts';

import { queryHook } from './SearchAutocompleteHeadless.util.ts';

interface UseSearchAutocompleteWithHistoryProps {
  domain?: SearchDomain;
  limit?: number;
  initialState: HistoryItem | null;
}

interface UseSearchAutocompleteWithHistoryResult extends Pick<
  AutocompleteProps<HistoryItem, false, false, true>,
  'freeSolo' | 'options' | 'inputValue' | 'onInputChange' | 'filterOptions' | 'onBlur'
> {
  removeRecentSearchItem: (value: string) => void;
}

export interface HistoryItem {
  label: string;
  id: string;
}

export const useSearchAutocompleteWithHistory = ({
  limit,
  domain,
  initialState,
}: UseSearchAutocompleteWithHistoryProps): UseSearchAutocompleteWithHistoryResult => {
  const { refine: debouncedSearch } = useSearchBox({ queryHook });
  const { refine: search } = useSearchBox();

  const [inputValue, setInputValue] = useState<string>(initialState?.label ?? '');

  const navigate = useNavigate();
  useEffect(() => {
    if (!domain) {
      clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain]);

  const [rawHistory, setRawHistory] = useLocalStorage<HistoryItem[]>(
    `${LOCAL_STORAGE_PREFIX}${LocalStorageKeys.SEARCH_AUTOCOMPLETE_RECENT}`,
    [],
  );

  const filteredHistory = useMemo(
    () => rawHistory.filter(({ label }) => label.includes(inputValue ?? '')),
    [rawHistory, inputValue],
  );

  const addItem = (value: string | undefined) => {
    if (isNil(value) || isEmpty(value)) {
      return;
    }
    const updatedList = [{ id: value, label: value }, ...rawHistory.filter(({ label }) => label !== value)];
    setRawHistory(updatedList);
  };

  const removeRecentSearchItem = (value: string) => {
    const updatedList = rawHistory.filter(({ label }) => label !== value);
    setRawHistory(updatedList);
  };

  const clear = () => {
    search('');
    setInputValue('');
  };

  const update = (value: string) => {
    setInputValue(value);
    search(value);
  };

  const selectOption = (value: string) => {
    navigateToFistIndex();
    addItem(value);
    update(value);
  };

  const handleUserInput = (value: string) => {
    navigateToFistIndex();
    debouncedSearch(value);
    setInputValue(value);
  };

  const navigateToFistIndex = () => {
    // search bar actions on the search landing page should
    // redirect any queries to the first index explicitly
    if (!domain) {
      navigate(RouteId.searchDetail(searchDomains[0]));
    }
  };

  return {
    onInputChange: (_: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
      if (inputValue && inputValue === value) {
        return;
      }

      if (reason === 'clear') {
        clear();
        return;
      }

      if (reason === 'reset') {
        update(value);
      }

      if (reason === 'selectOption') {
        selectOption(value);
      } else if (reason === 'input') {
        handleUserInput(value);
      }
    },
    filterOptions: (options: HistoryItem[]) => {
      return inputValue ? options.filter(({ label }) => label.includes(inputValue)) : options;
    },
    onBlur: () => addItem(inputValue),
    options: limit ? filteredHistory.slice(0, limit) : filteredHistory,
    inputValue,
    freeSolo: true,
    removeRecentSearchItem,
  };
};
